import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Printer, Loader2, FileText, Download, Mail } from 'lucide-react';
import SendEmailModal from '../../components/SendEmailModal';
import CargoArrivalNoticePrint from './CargoArrivalNoticePrint';
import { generateDocPdf } from '../../utils/generateDocPdf';
import { useToast } from '../../hooks/use-toast';

const CargoArrivalNoticeForm = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const printContainerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [docketInfo, setDocketInfo] = useState(null);
  
  const [formData, setFormData] = useState({
    job_no: '',
    date: '',
    shipper: '',
    consignee: '',
    notify: '',
    airline: '',
    flight_no: '',
    flight_date: '',
    airport_of_load: '',
    airport_of_discharge: '',
    mawb_no: '',
    mawb_date: '',
    hawb_no: '',
    hawb_date: '',
    no_of_packages: '',
    gross_weight: '',
    description: '',
    igm_no: '',
    igm_date: '',
    temperature: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Fetch docket data and existing CAN if any
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch docket print data (has all resolved names)
        const docketResponse = await fetch(`${backendUrl}/api/dockets/${docketId}/print-data`, {
          headers: getAuthHeaders()
        });
        
        if (!docketResponse.ok) {
          throw new Error('Failed to fetch docket data');
        }
        
        const docketData = await docketResponse.json();
        setDocketInfo(docketData);
        
        // Check if CAN already exists
        const canResponse = await fetch(`${backendUrl}/api/dockets/${docketId}/documents/CARGO_ARRIVAL_NOTICE`, {
          headers: getAuthHeaders()
        });
        
        if (canResponse.ok) {
          const canData = await canResponse.json();
          if (canData && canData.id) {
            // Existing CAN found - load it
            setFormData(canData.document_data);
            setDocumentId(canData.id);
            setIsSaved(true);
          } else {
            // No existing CAN - populate from docket
            populateFromDocket(docketData);
          }
        } else {
          // No existing CAN - populate from docket
          populateFromDocket(docketData);
        }
        
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    if (docketId) {
      fetchData();
    }
  }, [docketId, backendUrl, toast]);

  // Populate form from docket data
  const populateFromDocket = (docketData) => {
    const { docket, resolved_parties, resolved_shipment } = docketData;
    const payload = docket.payload_json || {};
    const header = payload.header || {};
    const shipmentInfo = payload.shipment_info || {};
    const invoices = payload.invoices || [];
    const invoice = invoices[0] || {};

    // Build shipper address string
    const shipperData = resolved_parties?.exporter_shipper;
    let shipperText = '';
    if (shipperData) {
      shipperText = [
        shipperData.name,
        shipperData.branch_name,
        shipperData.address,
        shipperData.phone ? `Tel: ${shipperData.phone}` : '',
        shipperData.email ? `Email: ${shipperData.email}` : ''
      ].filter(Boolean).join('\n');
    }

    // Build consignee address string
    const consigneeData = resolved_parties?.importer_receiver;
    let consigneeText = '';
    if (consigneeData) {
      consigneeText = [
        consigneeData.name,
        consigneeData.branch_name,
        consigneeData.address,
        consigneeData.phone ? `Tel: ${consigneeData.phone}` : '',
        consigneeData.email ? `Email: ${consigneeData.email}` : ''
      ].filter(Boolean).join('\n');
    }

    // Build notify party address string
    const notifyData = resolved_parties?.notify_party;
    let notifyText = '';
    if (notifyData) {
      notifyText = [
        notifyData.name,
        notifyData.branch_name,
        notifyData.address,
        notifyData.phone ? `Tel: ${notifyData.phone}` : '',
        notifyData.email ? `Email: ${notifyData.email}` : ''
      ].filter(Boolean).join('\n');
    }

    setFormData({
      job_no: docket.system_docket_no || '',
      date: new Date().toISOString().split('T')[0],
      shipper: shipperText,
      consignee: consigneeText,
      notify: notifyText,
      airline: resolved_shipment?.carrier || '',
      flight_no: shipmentInfo.flight_no || '',
      flight_date: shipmentInfo.flight_date || '',
      airport_of_load: resolved_shipment?.loading_from || '',
      airport_of_discharge: resolved_shipment?.discharge_at || '',
      mawb_no: shipmentInfo.mawb_no || '',
      mawb_date: shipmentInfo.mawb_date || '',
      hawb_no: shipmentInfo.hawb_no || '',
      hawb_date: shipmentInfo.hawb_date || '',
      no_of_packages: invoice.no_of_packages || '',
      gross_weight: invoice.gross_weight || '',
      description: invoice.description || shipmentInfo.hawb_description || '',
      igm_no: shipmentInfo.igm_no || '',
      igm_date: shipmentInfo.igm_date || '',
      temperature: header.temp || header.temperature || ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const url = isSaved 
        ? `${backendUrl}/api/dockets/${docketId}/documents/CARGO_ARRIVAL_NOTICE`
        : `${backendUrl}/api/dockets/${docketId}/documents`;
      
      const method = isSaved ? 'PUT' : 'POST';
      
      const body = isSaved 
        ? { document_data: formData }
        : { document_type: 'CARGO_ARRIVAL_NOTICE', document_data: formData };
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save');
      }
      
      const savedData = await response.json();
      setDocumentId(savedData.id);
      setIsSaved(true);
      
      toast({
        title: "Success",
        description: "Cargo Arrival Notice saved successfully"
      });
      
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePrintOrPDF = () => {
    navigate(`/dockets/${docketId}/can-print`);
  };

  const pdfFetcher = async () => {
    if (!printContainerRef.current) return null;
    const el = printContainerRef.current;
    el.style.left = '0';
    await new Promise(r => setTimeout(r, 1500));
    try {
      return await generateDocPdf(el);
    } finally {
      el.style.left = '-9999px';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-600" />
                Cargo Arrival Notice
              </h1>
              <p className="text-sm text-text-secondary font-ibm">
                Docket: {docketInfo?.docket?.system_docket_no || docketId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSaved && (
              <>
                <Button variant="outline" onClick={handlePrintOrPDF} data-testid="can-save-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Save PDF
                </Button>
                <Button variant="outline" onClick={handlePrintOrPDF} data-testid="can-print-btn">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" onClick={() => setShowEmailModal(true)}
                  className="border-orange-500 text-orange-600 hover:bg-orange-50" data-testid="can-send-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> Send Mail
                </Button>
              </>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Form */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 px-5 py-3">
            <h2 className="text-base font-semibold text-white font-ibm">CARGO ARRIVAL NOTICE</h2>
          </div>
          
          <div className="p-5 space-y-5">
            {/* Row 1: Job No, Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Job No.</Label>
                <Input 
                  value={formData.job_no}
                  readOnly
                  className="mt-1 bg-gray-50 cursor-not-allowed"
                  data-testid="can-job-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Date</Label>
                <Input 
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className="mt-1"
                  data-testid="can-date"
                />
              </div>
            </div>

            {/* Row 2: Shipper, Consignee, Notify */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Shipper</Label>
                <Textarea 
                  value={formData.shipper}
                  onChange={(e) => handleChange('shipper', e.target.value)}
                  className="mt-1 h-24"
                  placeholder="Shipper name and address"
                  data-testid="can-shipper"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Consignee</Label>
                <Textarea 
                  value={formData.consignee}
                  onChange={(e) => handleChange('consignee', e.target.value)}
                  className="mt-1 h-24"
                  placeholder="Consignee name and address"
                  data-testid="can-consignee"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Notify</Label>
                <Textarea 
                  value={formData.notify}
                  onChange={(e) => handleChange('notify', e.target.value)}
                  className="mt-1 h-24"
                  placeholder="Notify party"
                  data-testid="can-notify"
                />
              </div>
            </div>

            {/* Row 3: Airline, Flight No, Flight Date */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Flight Name / Airline</Label>
                <Input 
                  value={formData.airline}
                  onChange={(e) => handleChange('airline', e.target.value)}
                  className="mt-1"
                  data-testid="can-airline"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Flight No.</Label>
                <Input 
                  value={formData.flight_no}
                  onChange={(e) => handleChange('flight_no', e.target.value)}
                  className="mt-1"
                  data-testid="can-flight-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Flight Date</Label>
                <Input 
                  type="date"
                  value={formData.flight_date}
                  onChange={(e) => handleChange('flight_date', e.target.value)}
                  className="mt-1"
                  data-testid="can-flight-date"
                />
              </div>
            </div>

            {/* Row 4: Airport of Load, Airport of Discharge */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Airport of Load</Label>
                <Input 
                  value={formData.airport_of_load}
                  onChange={(e) => handleChange('airport_of_load', e.target.value)}
                  className="mt-1"
                  data-testid="can-airport-load"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Airport of Discharge</Label>
                <Input 
                  value={formData.airport_of_discharge}
                  onChange={(e) => handleChange('airport_of_discharge', e.target.value)}
                  className="mt-1"
                  data-testid="can-airport-discharge"
                />
              </div>
            </div>

            {/* Row 5: MAWB No, MAWB Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">MAWB No.</Label>
                <Input 
                  value={formData.mawb_no}
                  onChange={(e) => handleChange('mawb_no', e.target.value)}
                  className="mt-1"
                  data-testid="can-mawb-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">MAWB Date</Label>
                <Input 
                  type="date"
                  value={formData.mawb_date}
                  onChange={(e) => handleChange('mawb_date', e.target.value)}
                  className="mt-1"
                  data-testid="can-mawb-date"
                />
              </div>
            </div>

            {/* Row 6: HAWB No, HAWB Date, No. of Packages, Gross Weight */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">HAWB No.</Label>
                <Input 
                  value={formData.hawb_no}
                  onChange={(e) => handleChange('hawb_no', e.target.value)}
                  className="mt-1"
                  data-testid="can-hawb-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">HAWB Date</Label>
                <Input 
                  type="date"
                  value={formData.hawb_date}
                  onChange={(e) => handleChange('hawb_date', e.target.value)}
                  className="mt-1"
                  data-testid="can-hawb-date"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">No. of Packages</Label>
                <Input 
                  value={formData.no_of_packages}
                  onChange={(e) => handleChange('no_of_packages', e.target.value)}
                  className="mt-1"
                  data-testid="can-packages"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Gross Weight</Label>
                <Input 
                  value={formData.gross_weight}
                  onChange={(e) => handleChange('gross_weight', e.target.value)}
                  className="mt-1"
                  data-testid="can-gross-weight"
                />
              </div>
            </div>

            {/* Row 7: Description */}
            <div>
              <Label className="text-xs font-semibold text-text-secondary font-ibm">Description</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="mt-1 h-20"
                placeholder="Cargo description"
                data-testid="can-description"
              />
            </div>

            {/* Row 8: IGM No, IGM Date, Temperature */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">IGM No.</Label>
                <Input 
                  value={formData.igm_no}
                  onChange={(e) => handleChange('igm_no', e.target.value)}
                  className="mt-1"
                  data-testid="can-igm-no"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">IGM Date</Label>
                <Input 
                  type="date"
                  value={formData.igm_date}
                  onChange={(e) => handleChange('igm_date', e.target.value)}
                  className="mt-1"
                  data-testid="can-igm-date"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Temperature</Label>
                <Input 
                  value={formData.temperature}
                  onChange={(e) => handleChange('temperature', e.target.value)}
                  className="mt-1"
                  placeholder="e.g., 2-8°C"
                  data-testid="can-temperature"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Status indicator */}
        {isSaved && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between">
            <p className="text-sm text-emerald-700 font-ibm">
              <span className="font-semibold">Saved</span> — This Cargo Arrival Notice is linked to docket {docketInfo?.docket?.system_docket_no}
            </p>
            <span className="text-xs text-emerald-600">Document ID: {documentId?.slice(0, 8)}...</span>
          </div>
        )}
      </div>
    </div>

    {/* Hidden print container for PDF generation */}
    {isSaved && (
      <div
        ref={printContainerRef}
        style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', background: '#fff', zIndex: -1 }}
      >
        <CargoArrivalNoticePrint docketId={docketId} formData={formData} />
      </div>
    )}

    <SendEmailModal
      isOpen={showEmailModal}
      onClose={() => setShowEmailModal(false)}
      templateKey="can_issued"
      variables={{
        docket_no: docketInfo?.docket?.system_docket_no || '',
        consignee_name: (formData.consignee || '').split('\n')[0],
        shipper_name: (formData.shipper || '').split('\n')[0],
        description: formData.description_of_packages || '',
        packages: formData.no_of_packages || '',
        weight: formData.actual_weight || '',
      }}
      defaultToEmail={docketInfo?.resolved_parties?.importer_receiver?.email || ''}
      defaultSubject={`Cargo Arrival Notice — ${docketInfo?.docket?.system_docket_no || ''}`}
      pdfFetcher={isSaved ? pdfFetcher : null}
      pdfFilename={`CAN_${docketInfo?.docket?.system_docket_no || docketId}.pdf`}
      documentTitle={`Cargo Arrival Notice — ${docketInfo?.docket?.system_docket_no || ''}`}
      docketId={docketId}
    />
    </>
  );
};

export default CargoArrivalNoticeForm;
